import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import API from '../../../services/API'
import {
  CUSTOMER_CONTROL_HINT,
  CUSTOMER_CONTROL_TYPES,
  HAZARD_TYPE_OPTIONS as hazardTypeOptions,
  LIKELIHOOD_OPTIONS as likelihoodOptions,
  MODULE_OPTIONS as moduleOptions,
  REGISTERS,
  REGISTER_OPTIONS,
  RISK_ACCEPTABILITY_OPTIONS as riskAcceptabilityOptions,
  SEVERITY_OPTIONS as severityOptions,
  SOURCE_OPTIONS as sourceOptions,
  STATUS_OPTIONS as statusOptions,
  riskRatingFor,
  riskRatingLabel,
} from './constants'

const statusColorMap = {
  Open: 'error',
  'In Progress': 'warning',
  Mitigated: 'success',
  Accepted: 'info',
}

const riskColorMap = {
  Low: 'success',
  Medium: 'warning',
  High: 'error',
  Critical: 'error',
}

const emptyRisk = {
  likelihood: '',
  severity: '',
  riskRating: '',
}

const emptyForm = {
  register: 'Clinical',
  title: '',
  module: '',
  hazardType: 'Clinical Safety',
  source: '',
  description: '',
  cause: [],
  clinicalImpact: [],
  controlMeasures: [],
  initialRisk: { ...emptyRisk },
  residualRisk: { ...emptyRisk },
  status: 'Open',
  riskAcceptability: '',
  linkedFeatures: [],
  notes: '',
  customerFacing: { include: false, controlTypes: [], summary: '', guidance: '' },
}

const formFromHazard = (data) => ({
  register: REGISTER_OPTIONS.includes(data.register) ? data.register : 'Clinical',
  title: data.title || '',
  module: data.module || '',
  hazardType: data.hazardType || 'Clinical Safety',
  source: data.source || '',
  description: data.description || '',
  cause: data.cause || [],
  clinicalImpact: data.clinicalImpact || [],
  controlMeasures: data.controlMeasures || [],
  initialRisk: data.initialRisk || { ...emptyRisk },
  residualRisk: data.residualRisk || { ...emptyRisk },
  status: data.status || 'Open',
  riskAcceptability: data.riskAcceptability || '',
  linkedFeatures: data.linkedFeatures || [],
  notes: data.notes || '',
  customerFacing: {
    include: !!data.customerFacing?.include,
    controlTypes: data.customerFacing?.controlTypes || [],
    summary: data.customerFacing?.summary || '',
    guidance: data.customerFacing?.guidance || '',
  },
})

const arrayToText = (arr) => (arr || []).join('\n')
const textToArray = (text) =>
  String(text || '')
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean)

const riskToneMap = {
  Low: '#27803B',
  Medium: '#B54708',
  High: '#B42318',
  Critical: '#7A271A',
}

const ambuTeal = {
  main: '#239AA4',
  dark: '#0F6670',
  deep: '#123F4A',
  light: '#4DBBC4',
}

const SectionPaper = ({ children, sx }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, md: 3 },
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'rgba(35, 154, 164, 0.16)',
      boxShadow: '0 14px 32px rgba(15, 102, 112, 0.08)',
      '& .MuiInputBase-input.Mui-disabled': {
        WebkitTextFillColor: '#263238',
        color: '#263238',
        opacity: 1,
      },
      '& .MuiInputLabel-root.Mui-disabled': {
        color: '#6C7A89',
      },
      '& .MuiOutlinedInput-root.Mui-disabled': {
        backgroundColor: '#FAFBFA',
      },
      ...sx,
    }}
  >
    {children}
  </Paper>
)

const SectionHeader = ({ icon, title, caption, action }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={1.5}
    justifyContent="space-between"
    alignItems={{ xs: 'flex-start', sm: 'center' }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1.5,
          display: 'grid',
          placeItems: 'center',
          color: ambuTeal.dark,
          backgroundColor: 'rgba(35, 154, 164, 0.12)',
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h6" fontWeight={800}>
          {title}
        </Typography>
        {caption && (
          <Typography variant="body2" color="text.secondary">
            {caption}
          </Typography>
        )}
      </Box>
    </Stack>
    {action}
  </Stack>
)

const RiskSnapshot = ({ title, risk }) => {
  const rating = riskRatingFor(risk?.likelihood, risk?.severity) || risk?.riskRating || 'Not scored'
  const tone = riskToneMap[rating] || ambuTeal.dark

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: `${tone}33`,
        backgroundColor: `${tone}0F`,
        minWidth: 0,
      }}
    >
      <Stack spacing={1}>
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <WarningAmberIcon sx={{ color: tone }} fontSize="small" />
          <Typography variant="h5" fontWeight={900} color={tone}>
            {rating}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {[risk?.likelihood, risk?.severity].filter(Boolean).join(' / ') ||
            'Likelihood and severity not recorded'}
        </Typography>
      </Stack>
    </Paper>
  )
}

const SingleHazardLog = () => {
  const { hazardID: id } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [hazard, setHazard] = useState(null)
  const [reviewComment, setReviewComment] = useState('')

  const [form, setForm] = useState(emptyForm)
  const [causeText, setCauseText] = useState('')
  const [impactText, setImpactText] = useState('')
  const [linkedFeaturesText, setLinkedFeaturesText] = useState('')

  const loadHazard = () => {
    setLoading(true)
    setError(false)

    API.get(`/admincrm/clinical-risk/hazard-log/${id}`)
      .then((res) => {
        if (res.status === 200) {
          const data = res.data
          setHazard(data)
          setForm(formFromHazard(data))
          setCauseText(arrayToText(data.cause))
          setImpactText(arrayToText(data.clinicalImpact))
          setLinkedFeaturesText(arrayToText(data.linkedFeatures))
        } else {
          setError(true)
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadHazard()
  }, [id])

  const handleRiskChange = (section, field, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }))
  }

  const handleControlChange = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      controlMeasures: prev.controlMeasures.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    }))
  }

  const addControlMeasure = () => {
    setForm((prev) => ({
      ...prev,
      controlMeasures: [
        ...(prev.controlMeasures || []),
        {
          type: 'existing',
          control: '',
          description: '',
          implemented: false,
        },
      ],
    }))
  }

  const removeControlMeasure = (index) => {
    setForm((prev) => ({
      ...prev,
      controlMeasures: prev.controlMeasures.filter((_, i) => i !== index),
    }))
  }

  const handleSave = () => {
    setSaving(true)

    const payload = {
      ...form,
      cause: textToArray(causeText),
      clinicalImpact: textToArray(impactText),
      linkedFeatures: textToArray(linkedFeaturesText),
      reviewComment,
    }

    API.put(`/admincrm/clinical-risk/hazard-log/${id}`, payload)
      .then((res) => {
        if (res.status === 200) {
          setHazard(res.data)
          setEditMode(false)
          setReviewComment('')
          setForm(formFromHazard(res.data))
          setCauseText(arrayToText(res.data.cause))
          setImpactText(arrayToText(res.data.clinicalImpact))
          setLinkedFeaturesText(arrayToText(res.data.linkedFeatures))
        }
      })
      .finally(() => setSaving(false))
  }

  const headerActions = (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(REGISTERS[form.register]?.path || '/clinical-risk')}
        sx={{ borderColor: 'rgba(255,255,255,0.42)', color: '#fff' }}
      >
        {REGISTERS[form.register]?.short || 'Back'} register
      </Button>
      {!editMode ? (
        <Button
          variant="contained"
          startIcon={<EditOutlinedIcon />}
          onClick={() => setEditMode(true)}
          sx={{
            bgcolor: '#fff',
            color: ambuTeal.deep,
            fontWeight: 800,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.88)' },
          }}
        >
          Edit
        </Button>
      ) : (
        <>
          <Button
            variant="outlined"
            sx={{ borderColor: 'rgba(255,255,255,0.42)', color: '#fff' }}
            onClick={() => {
              setEditMode(false)
              setReviewComment('')
              if (hazard) {
                setForm(formFromHazard(hazard))
                setCauseText(arrayToText(hazard.cause))
                setImpactText(arrayToText(hazard.clinicalImpact))
                setLinkedFeaturesText(arrayToText(hazard.linkedFeatures))
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveOutlinedIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </>
      )}
    </Stack>
  )

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !hazard) {
    return <Alert severity="error">Unable to load hazard.</Alert>
  }

  return (
    <Stack spacing={2.5}>
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          color: '#fff',
          background: 'linear-gradient(135deg, #0F6670 0%, #239AA4 52%, #123F4A 100%)',
          boxShadow: '0 18px 42px rgba(15, 102, 112, 0.2)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
          spacing={2}
        >
          <Box minWidth={0}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
              <Typography variant="h4" fontWeight={900} lineHeight={1.1} sx={{ color: '#fff' }}>
                {hazard.hazardID}
              </Typography>
              <Chip
                label={editMode ? form.status : hazard.status}
                size="small"
                color={statusColorMap[editMode ? form.status : hazard.status] || 'default'}
                sx={{ borderRadius: 1, fontWeight: 800 }}
              />
              <Chip
                label={editMode ? form.module : hazard.module}
                size="small"
                sx={{
                  borderRadius: 1,
                  bgcolor: 'rgba(255,255,255,0.16)',
                  color: '#fff',
                  fontWeight: 700,
                }}
              />
              <Chip
                label={`${REGISTERS[form.register]?.short || 'Clinical'} register`}
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: 1,
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: '#fff',
                  fontWeight: 700,
                }}
              />
            </Stack>
            <Typography variant="h6" fontWeight={700} sx={{ maxWidth: 900, color: '#F7FAF8' }}>
              {editMode ? form.title : hazard.title}
            </Typography>
            <Typography variant="body2" sx={{ color: '#DDE8E2', mt: 1 }}>
              {hazard.updatedAt
                ? `Last updated ${moment(hazard.updatedAt).format('DD/MM/YYYY HH:mm')}`
                : 'Review and mitigation record'}
            </Typography>
          </Box>
          {headerActions}
        </Stack>
      </Box>
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <RiskSnapshot title="Initial Risk" risk={form.initialRisk} />
          </Grid>
          <Grid item xs={12} md={6}>
            <RiskSnapshot title="Residual Risk" risk={form.residualRisk} />
          </Grid>
        </Grid>
      </Box>
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <SectionPaper>
              <Stack spacing={3}>
                <SectionHeader
                  icon={<ShieldOutlinedIcon fontSize="small" />}
                  title="Hazard Details"
                  caption="Core hazard definition, cause, impact, and linked product areas"
                />
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <TextField
                        label="Title"
                        fullWidth
                        value={form.title}
                        disabled={!editMode}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            title: e.target.value,
                          }))
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        label="Module"
                        fullWidth
                        value={form.module}
                        disabled={!editMode}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            module: e.target.value,
                          }))
                        }
                      >
                        {moduleOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        label="Register"
                        fullWidth
                        value={form.register}
                        disabled={!editMode}
                        helperText={
                          form.register === 'Codebase'
                            ? 'Found by examining the software'
                            : 'About the product or organisation'
                        }
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            register: e.target.value,
                          }))
                        }
                      >
                        {REGISTER_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {REGISTERS[option].label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        label="Hazard Type"
                        fullWidth
                        value={form.hazardType}
                        disabled={!editMode}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            hazardType: e.target.value,
                          }))
                        }
                      >
                        {hazardTypeOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        label="Source"
                        fullWidth
                        value={form.source}
                        disabled={!editMode}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            source: e.target.value,
                          }))
                        }
                      >
                        <MenuItem value="">
                          <em>Not specified</em>
                        </MenuItem>
                        {sourceOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Description"
                        fullWidth
                        multiline
                        minRows={4}
                        value={form.description}
                        disabled={!editMode}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Cause"
                        fullWidth
                        multiline
                        minRows={5}
                        helperText="One item per line"
                        value={causeText}
                        disabled={!editMode}
                        onChange={(e) => setCauseText(e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Clinical Impact"
                        fullWidth
                        multiline
                        minRows={5}
                        helperText="One item per line"
                        value={impactText}
                        disabled={!editMode}
                        onChange={(e) => setImpactText(e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Linked Features"
                        fullWidth
                        multiline
                        minRows={3}
                        helperText="One feature/task per line"
                        value={linkedFeaturesText}
                        disabled={!editMode}
                        onChange={(e) => setLinkedFeaturesText(e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Notes"
                        fullWidth
                        multiline
                        minRows={4}
                        value={form.notes}
                        disabled={!editMode}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            notes: e.target.value,
                          }))
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                <Stack spacing={2}>
                  <SectionHeader
                    icon={<HandshakeOutlinedIcon fontSize="small" />}
                    title="Customer hazard pack (DCB0160)"
                    caption="Whether deploying organisations are told about this hazard, and what they are told. Notes, causes and evidence never leave the CRM."
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!form.customerFacing?.include}
                        disabled={!editMode}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            customerFacing: { ...prev.customerFacing, include: e.target.checked },
                          }))
                        }
                      />
                    }
                    label={
                      form.customerFacing?.include
                        ? 'Included in the customer hazard pack'
                        : 'Not included in the customer hazard pack'
                    }
                  />
                  {form.customerFacing?.include && (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          What the organisation must do
                        </Typography>
                        <Stack direction="row" gap={1} flexWrap="wrap">
                          {CUSTOMER_CONTROL_TYPES.map((type) => {
                            const selected = (form.customerFacing?.controlTypes || []).includes(
                              type,
                            )
                            return (
                              <Tooltip key={type} title={CUSTOMER_CONTROL_HINT[type]}>
                                <Chip
                                  label={type}
                                  color={selected ? 'primary' : 'default'}
                                  variant={selected ? 'filled' : 'outlined'}
                                  disabled={!editMode}
                                  onClick={
                                    editMode
                                      ? () =>
                                          setForm((prev) => {
                                            const current = prev.customerFacing?.controlTypes || []
                                            return {
                                              ...prev,
                                              customerFacing: {
                                                ...prev.customerFacing,
                                                controlTypes: selected
                                                  ? current.filter((t) => t !== type)
                                                  : [...current, type],
                                              },
                                            }
                                          })
                                      : undefined
                                  }
                                  sx={{ borderRadius: 1.5 }}
                                />
                              </Tooltip>
                            )
                          })}
                        </Stack>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Customer-facing summary"
                          fullWidth
                          multiline
                          minRows={3}
                          helperText="Replaces the description in the pack. Leave blank to show the description as written above."
                          value={form.customerFacing?.summary || ''}
                          disabled={!editMode}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              customerFacing: { ...prev.customerFacing, summary: e.target.value },
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Guidance for the organisation"
                          fullWidth
                          multiline
                          minRows={4}
                          helperText="Shown verbatim in the pack. Write it for their clinical safety officer: what to configure, train, or put a procedure around."
                          value={form.customerFacing?.guidance || ''}
                          disabled={!editMode}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              customerFacing: { ...prev.customerFacing, guidance: e.target.value },
                            }))
                          }
                        />
                      </Grid>
                    </Grid>
                  )}
                </Stack>

                <Divider />

                <Stack spacing={2}>
                  <SectionHeader
                    icon={<FactCheckOutlinedIcon fontSize="small" />}
                    title="Control Measures"
                    caption={`${(form.controlMeasures || []).length} recorded mitigation${
                      (form.controlMeasures || []).length === 1 ? '' : 's'
                    }`}
                    action={
                      editMode && (
                        <Button variant="outlined" onClick={addControlMeasure}>
                          Add Control
                        </Button>
                      )
                    }
                  />

                  {(form.controlMeasures || []).length === 0 && (
                    <Alert severity="info">No control measures added yet.</Alert>
                  )}

                  {(form.controlMeasures || []).map((item, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        borderColor: 'rgba(35, 154, 164, 0.16)',
                        backgroundColor: item.type === 'planned' ? '#FFF8EB' : '#F4FBFC',
                      }}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <TextField
                            select
                            label="Type"
                            fullWidth
                            value={item.type || 'existing'}
                            disabled={!editMode}
                            onChange={(e) => handleControlChange(index, 'type', e.target.value)}
                          >
                            <MenuItem value="existing">Existing</MenuItem>
                            <MenuItem value="planned">Planned</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={9}>
                          <TextField
                            label="Control"
                            fullWidth
                            value={item.control || ''}
                            disabled={!editMode}
                            onChange={(e) => handleControlChange(index, 'control', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Description"
                            fullWidth
                            multiline
                            minRows={3}
                            value={item.description || ''}
                            disabled={!editMode}
                            onChange={(e) =>
                              handleControlChange(index, 'description', e.target.value)
                            }
                          />
                        </Grid>
                        {editMode && (
                          <Grid item xs={12}>
                            <Button color="error" onClick={() => removeControlMeasure(index)}>
                              Remove
                            </Button>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
            </SectionPaper>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={2}>
              <SectionPaper>
                <Stack spacing={2}>
                  <SectionHeader
                    icon={<TimelineOutlinedIcon fontSize="small" />}
                    title="Risk Scoring"
                    caption="Review rating, status, and acceptability"
                  />

                  <Typography variant="subtitle2">Initial Risk</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        select
                        label="Likelihood"
                        fullWidth
                        value={form.initialRisk?.likelihood || ''}
                        disabled={!editMode}
                        onChange={(e) =>
                          handleRiskChange('initialRisk', 'likelihood', e.target.value)
                        }
                      >
                        {likelihoodOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        select
                        label="Severity"
                        fullWidth
                        value={form.initialRisk?.severity || ''}
                        disabled={!editMode}
                        onChange={(e) =>
                          handleRiskChange('initialRisk', 'severity', e.target.value)
                        }
                      >
                        {severityOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Risk Rating"
                        fullWidth
                        value={riskRatingLabel(form.initialRisk)}
                        disabled
                        placeholder="Pick likelihood and severity"
                        helperText="Set automatically from the CRMP risk matrix"
                      />
                    </Grid>
                  </Grid>

                  <Divider />

                  <Typography variant="subtitle2">Residual Risk</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        select
                        label="Likelihood"
                        fullWidth
                        value={form.residualRisk?.likelihood || ''}
                        disabled={!editMode}
                        onChange={(e) =>
                          handleRiskChange('residualRisk', 'likelihood', e.target.value)
                        }
                      >
                        {likelihoodOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        select
                        label="Severity"
                        fullWidth
                        value={form.residualRisk?.severity || ''}
                        disabled={!editMode}
                        onChange={(e) =>
                          handleRiskChange('residualRisk', 'severity', e.target.value)
                        }
                      >
                        {severityOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Risk Rating"
                        fullWidth
                        value={riskRatingLabel(form.residualRisk)}
                        disabled
                        placeholder="Pick likelihood and severity"
                        helperText="Set automatically from the CRMP risk matrix"
                      />
                    </Grid>
                  </Grid>

                  <Divider />

                  <TextField
                    select
                    label="Status"
                    fullWidth
                    value={form.status}
                    disabled={!editMode}
                    onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    label="Risk Acceptability"
                    fullWidth
                    value={form.riskAcceptability}
                    disabled={!editMode}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        riskAcceptability: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="">
                      <em>Not yet determined</em>
                    </MenuItem>
                    {riskAcceptabilityOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>

                  {editMode && (
                    <TextField
                      label="Review Comment"
                      fullWidth
                      multiline
                      minRows={4}
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      helperText="Optional note for the review history"
                    />
                  )}
                </Stack>
              </SectionPaper>

              <SectionPaper>
                <Stack spacing={2}>
                  <SectionHeader
                    icon={<TimelineOutlinedIcon fontSize="small" />}
                    title="Review History"
                    caption="Most recent review first"
                  />

                  {!hazard.reviewHistory?.length && (
                    <Alert severity="info">No review history recorded.</Alert>
                  )}

                  {(hazard.reviewHistory || [])
                    .slice()
                    .reverse()
                    .map((review, index) => (
                      <Paper
                        key={index}
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          borderColor: 'rgba(35, 154, 164, 0.16)',
                          backgroundColor: '#fff',
                        }}
                      >
                        <Stack spacing={1}>
                          <Typography variant="body2" fontWeight={700}>
                            {moment(review.date).format('DD/MM/YYYY HH:mm')}
                          </Typography>

                          <Stack direction="row" spacing={1}>
                            <Chip
                              size="small"
                              label={review.status || 'Unknown'}
                              color={statusColorMap[review.status] || 'default'}
                              sx={{ borderRadius: 1 }}
                            />
                            {review.residualRisk?.riskRating && (
                              <Chip
                                size="small"
                                label={`Residual: ${review.residualRisk.riskRating}`}
                                color={riskColorMap[review.residualRisk.riskRating] || 'default'}
                                variant="outlined"
                                sx={{ borderRadius: 1 }}
                              />
                            )}
                          </Stack>

                          {review.comment && (
                            <Typography variant="body2" color="text.secondary">
                              {review.comment}
                            </Typography>
                          )}

                          {review.reviewedBy && (
                            <Typography variant="caption" color="text.secondary">
                              Reviewed by {review.reviewedBy.firstName} {review.reviewedBy.lastName}
                            </Typography>
                          )}
                        </Stack>
                      </Paper>
                    ))}
                </Stack>
              </SectionPaper>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  )
}

export default SingleHazardLog
